import { Heading, Text, Flex } from "@amzn/storm-ui";
import { range } from "lodash";
import styled from "styled-components";

import { StyleConstants } from "../../../../config/constants";
import {
    useAiRecommendedInsightsLoader,
    type CardWithRecommendation,
} from "../../../../hooks/aiRecommendedInsights/useAiRecommendedInsightsLoader";
import { isLoading } from "../../../../utils/api.utils";
import SkeletonLoader from "../../../common/SkeletonLoader";
import { UnifiedContentCard } from "../unifiedContent/UnifiedContentCard";
import {
    StyledBackground,
    StyledInsightContentGridRenderer,
} from "../withInsightsRenderer";

const StyledAiResultsViewer = styled(StyledBackground)`
    padding-top: ${({ theme }) => theme.spacing.small.default};
    margin-bottom: ${({ theme }) => theme.spacing.small.default};
`;

const StyledSectionContainer = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing.small.default};
`;

const SectionHeader = styled(Flex)`
    align-items: center;
    margin-bottom: ${({ theme }) => theme.spacing.xsmall.default};
`;

export const AiResultsViewer = (): JSX.Element => {
    const [loadingState, cards, recommendations] =
        useAiRecommendedInsightsLoader();

    const getCardForContent = (
        content: CardWithRecommendation,
        index: number,
    ) => {
        const streamedRecommendation = content.identifier
            ? recommendations.get(content.identifier) || ""
            : "";
        const cardWithRecommendation: CardWithRecommendation = {
            ...content,
            recommendation:
                streamedRecommendation || content.recommendation || "",
        };

        return (
            <UnifiedContentCard
                key={`${content.identifier}-${index}`}
                content={cardWithRecommendation}
                index={index}
            />
        );
    };

    const isError = loadingState === "error";
    const hasContent = cards.length > 0;
    const showGrid = isLoading(loadingState) || hasContent;

    return (
        <StyledAiResultsViewer data-testid="ai-results-viewer">
            <StyledSectionContainer>
                <SectionHeader columnGap="xsmall">
                    <Heading renderAs="h4" size="small">
                        AI Results
                    </Heading>
                    {hasContent && !isLoading(loadingState) && (
                        <Text>({cards.length} results)</Text>
                    )}
                </SectionHeader>

                {showGrid && !isError && (
                    <StyledInsightContentGridRenderer
                        maxResults={4}
                        noMarginTop
                        hidden={false}
                    >
                        {range(0, 4).map((index) => {
                            const card = cards[index];

                            if (card) {
                                return getCardForContent(card, index);
                            }

                            return (
                                <SkeletonLoader
                                    height={`${StyleConstants.InsightCardSize}px`}
                                    width="100%"
                                    key={`skeleton-${index}`}
                                />
                            );
                        })}
                    </StyledInsightContentGridRenderer>
                )}

                {isError && (
                    <Text color="secondary">Failed to load AI results</Text>
                )}
            </StyledSectionContainer>
        </StyledAiResultsViewer>
    );
};
